/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  BreakpointsDesktop,
  BreakpointsTablet,
  BreakpointsMobileV,
} from '@wix/responsive-editor-symbols';
import {
  DropdownBase,
  TextButton,
  listItemActionBuilder,
  Text,
  Box,
} from '@wix/design-system';
import { ChevronDown } from '@wix/wix-ui-icons-common';
import { BreakpointsContext } from '../App/BreakpointsContext';
import { DocumentServicesContext } from '../App/DocumentServicesContext';
import { ClientContext } from '../App/ClientContext';
import {
  BREAKPOINT_MAX_VALUE,
  BREAKPOINT_MAX_ID,
  TPA_DESKTOP_WIDTH_THRESHOLD,
  EDITOR_TYPE,
} from '../../utils/constants';
import * as documentAPIs from '../../utils/documentAPI';
import s from './Breakpoints.scss';
import logger from '../../utils/biLogger';
import BreakpointsStudioDesktop from '../../assets/images/BreakpointsStudioDesktop.svg';
import BreakpointsStudioTablet from '../../assets/images/BreakpointsStudioTablet.svg';
import BreakpointsStudioMobileV from '../../assets/images/BreakpointsStudioMobileV.svg';
import {
  getFeedbackChooseMode,
  getFeedbackClickOnHeader,
} from '@wix/bi-logger-get-feedback/v2';
import { useAppContext } from '../App/AppContext';

function Breakpoints({ breakpoints }) {
  const { t } = useTranslation();

  const { setBreakPoint, currentBreakPoint } = useContext(BreakpointsContext);
  const { documentServices } = useContext(DocumentServicesContext);
  const {
    state: { editorType, role },
  } = useAppContext();
  const {
    user: { contactId },
  } = useContext(ClientContext);

  const isSiteEditorTypeStudio = () => editorType === EDITOR_TYPE.STUDIO;

  function breakpointsRange() {
    const filteredBreakpointsValues = breakpoints.values.filter(
      breakpoint => breakpoint.max < 9999,
    );
    const sorted = _.sortBy(
      filteredBreakpointsValues,
      bp => bp.max || Infinity,
    );
    const documentBreakpoints = sorted
      .map((bp, index) => {
        if (index !== 0) {
          const prevMax = sorted[index - 1].max;
          return { ...bp, min: prevMax + 1 };
        }
        return bp;
      })
      .reverse();
    documentBreakpoints.unshift({
      min: _.get(documentBreakpoints[0], 'max', 0) + 1,
      max: BREAKPOINT_MAX_VALUE,
      id: BREAKPOINT_MAX_ID,
    });
    return documentBreakpoints;
  }

  useEffect(() => {
    setBreakPoint(breakpointsRange()[0]);
  }, []);

  function isSelected(id) {
    return currentBreakPoint.id === id;
  }

  function renderBreakPointSVG(max, min, id) {
    if (max === BREAKPOINT_MAX_VALUE) {
      if (isSiteEditorTypeStudio()) {
        return (
          <BreakpointsStudioDesktop
            className={isSelected(BREAKPOINT_MAX_ID) ? s.selected : ''}
          />
        );
      }
      return (
        <BreakpointsDesktop
          className={isSelected(BREAKPOINT_MAX_ID) ? s.selected : ''}
        />
      );
    } else if (max <= 758) {
      if (isSiteEditorTypeStudio()) {
        return (
          <BreakpointsStudioMobileV
            className={isSelected(BREAKPOINT_MAX_ID) ? s.selected : ''}
          />
        );
      }
      return (
        <BreakpointsMobileV className={isSelected(id) ? s.selected : ''} />
      );
    } else if (max < 1240) {
      if (isSiteEditorTypeStudio()) {
        return (
          <BreakpointsStudioTablet
            className={isSelected(BREAKPOINT_MAX_ID) ? s.selected : ''}
          />
        );
      }
      return <BreakpointsTablet className={isSelected(id) ? s.selected : ''} />;
    } else {
      if (isSiteEditorTypeStudio()) {
        return (
          <BreakpointsStudioDesktop
            className={isSelected(BREAKPOINT_MAX_ID) ? s.selected : ''}
          />
        );
      }
      return (
        <BreakpointsDesktop className={isSelected(id) ? s.selected : ''} />
      );
    }
  }

  function renderBreakPointTitle(max, min) {
    if (max === BREAKPOINT_MAX_VALUE) {
      return t('get_feedback_header_breakpoints_desktop');
    } else if (max <= 758) {
      return t('get_feedback_header_breakpoints_mobile');
    } else if (max < 1240) {
      return t('get_feedback_header_breakpoints_tablet');
    } else {
      return t('get_feedback_header_breakpoints_desktop');
    }
  }

  function getBreakPointsOptions() {
    return breakpointsRange().map(breakpoint => {
      const { max, min, id } = breakpoint;
      const breakpointTitle = renderBreakPointTitle(max, min);
      return listItemActionBuilder({
        size: 'medium',
        id,
        skin: 'dark',
        className: isSiteEditorTypeStudio() ? s.rootStudioBGColor : '',
        value: breakpointTitle,
        prefixIcon: renderBreakPointSVG(max, min, id),
        onClick: () => {
          logger.report(
            getFeedbackChooseMode({
              user_type: role,
              editorVersion: editorType,
              mode: breakpointTitle,
              breakPoint: JSON.stringify({ max, min, id }),
              ...(contactId && { contactId }),
              ...(contactId
                ? { _client_id: contactId }
                : { _client_id: uuid() }),
            }),
          );
          setBreakPoint(breakpoint);
          const isDesktop =
            max > TPA_DESKTOP_WIDTH_THRESHOLD || max === BREAKPOINT_MAX_VALUE;
          documentServices.tpa.setDeviceType(isDesktop ? 'desktop' : 'mobile');
        },
        title: isSiteEditorTypeStudio() ? (
          <Box className={s.breakpointsDropdown}>
            <Text size="small" weight="normal" className={s.whiteTextColor}>
              {breakpointTitle}
            </Text>
            <Text
              size="small"
              weight="normal"
              className={`${s.studioWindowDataTextColor} ${s.alignSelfCenter}`}
            >
              W {max === BREAKPOINT_MAX_VALUE ? window.innerWidth : max}
            </Text>
          </Box>
        ) : (
          breakpointTitle
        ),
      });
    });
  }

  function getSelectedBreakPointSVG() {
    const { max, min, id } = currentBreakPoint;
    return renderBreakPointSVG(max, min, id);
  }

  function getSelectedBreakPointTitle() {
    const { max, min } = currentBreakPoint;
    return renderBreakPointTitle(max, min);
  }
  return (
    <>
      <ul
        className={`${s.breakpointsPreviewBar} ${
          isSiteEditorTypeStudio() ? s.studioBreakpointsPreviewBar : ''
        }`}
        data-hook="breakpoint-preview-bar"
      >
        <li>
          <div
            className={isSiteEditorTypeStudio() ? s.studioDivider : s.divider}
          ></div>
        </li>
        <li
          style={{
            width: isSiteEditorTypeStudio() ? '214px' : '131px',
          }}
        >
          <DropdownBase
            placement="bottom"
            data-hook="story-dropdown-base-uncontrolled-click"
            options={getBreakPointsOptions()}
            className={isSiteEditorTypeStudio() ? s.DropdownBase : ''}
          >
            {({ toggle }) => {
              return (
                <div
                  style={
                    isSiteEditorTypeStudio()
                      ? {
                          display: 'flex',
                          alignItems: 'center',
                          paddingLeft: '17px',
                          paddingRight: '7px',
                          height: '44px',
                        }
                      : {}
                  }
                  onClick={() => {
                    logger.report(
                      getFeedbackClickOnHeader({
                        evid: 1131,
                        user_type: role,
                        editorVersion: editorType,
                        ...(contactId && { contactId }),
                        ...(contactId
                          ? { _client_id: contactId }
                          : { _client_id: uuid() }),
                      }),
                    );
                    toggle();
                  }}
                >
                  <TextButton
                    size={isSiteEditorTypeStudio() ? 'small' : 'medium'}
                    weight="normal"
                    skin={isSiteEditorTypeStudio() ? 'light' : 'dark'}
                    suffixIcon={
                      isSiteEditorTypeStudio() ? null : <ChevronDown />
                    }
                    prefixIcon={
                      isSiteEditorTypeStudio() ? (
                        <Box width="24px" height="24px">
                          {getSelectedBreakPointSVG()}
                        </Box>
                      ) : (
                        getSelectedBreakPointSVG()
                      )
                    }
                    dataHook="drop-down-opener"
                  >
                    {getSelectedBreakPointTitle()}
                  </TextButton>
                  {isSiteEditorTypeStudio() && (
                    <>
                      <Box width="8px"></Box>
                      <Text
                        size="small"
                        weight="normal"
                        className={
                          isSiteEditorTypeStudio()
                            ? s.studioWindowDataTextColor
                            : ''
                        }
                      >
                        W
                      </Text>
                      <Text
                        size="small"
                        weight="normal"
                        className={`${s.widthTitle} ${
                          isSiteEditorTypeStudio()
                            ? s.studioWindowDataTextColor
                            : ''
                        }`}
                      >
                        {currentBreakPoint.max === BREAKPOINT_MAX_VALUE
                          ? window.innerWidth
                          : documentAPIs.calcBreakpointWidth({
                              ...currentBreakPoint,
                            })}
                        <span style={{ paddingTop: '4px' }}>px</span>
                      </Text>
                      <ChevronDown color="rgba(255, 255, 255, 0.70)" />
                    </>
                  )}
                </div>
              );
            }}
          </DropdownBase>
        </li>
        {isSiteEditorTypeStudio() ? null : (
          <>
            <li>
              <div className={s.divider}></div>
            </li>
            <li>
              <Text>W</Text>
            </li>
            <li style={{ width: '117px', paddingRight: '7px' }}>
              <Text className={s.widthTitle}>
                {currentBreakPoint.max === BREAKPOINT_MAX_VALUE
                  ? window.innerWidth
                  : documentAPIs.calcBreakpointWidth({ ...currentBreakPoint })}
                <span style={{ paddingTop: '4px' }}>px</span>
              </Text>
            </li>
          </>
        )}
        <li>
          <div
            className={isSiteEditorTypeStudio() ? s.studioDivider : s.divider}
          ></div>
        </li>
      </ul>
    </>
  );
}

export default Breakpoints;
